import api from 'api'
import { WORK_ORDER_CACHE_INVALIDATION_TAGS, withDelayedWorkOrderInvalidation } from 'api/cacheInvalidationTagSets'
import {
  CACHE_TAG_CONFIGURED_SERVICE_CONTAINER_LIST,
  CACHE_TAG_CONFIGURED_SERVICE_DETAILS,
  CACHE_TAG_CONFIGURED_SERVICE_EVENT_LIST,
  CACHE_TAG_CONFIGURED_SERVICES_LIST,
  CACHE_TAG_CONTAINER_VERSION_LIST,
  CACHE_TAG_EVENTS_VERSION_LIST,
  CACHE_TAG_SERVICE_EXPANDABLE_VIEW,
} from 'api/cacheTagTypes'
import { stampPlacementFlagsOnDraft } from 'components/inventory/containers/__mocks__/placementFlags'
import { withIdTag } from 'utils/cache'

const extendedApi = api.injectEndpoints({
  endpoints: build => ({
    getDraftConfiguredServices: build.query({
      query: () => ({
        url: '/api/v1/core/draft-configured-service',
      }),
      providesTags: [CACHE_TAG_CONFIGURED_SERVICES_LIST],
    }),
    createDraftConfiguredService: build.mutation({
      query: body => ({
        url: '/api/v1/core/draft-configured-service',
        method: 'POST',
        body,
      }),
      invalidatesTags: result => [
        CACHE_TAG_CONFIGURED_SERVICES_LIST,
        CACHE_TAG_SERVICE_EXPANDABLE_VIEW,
        ...withIdTag(CACHE_TAG_EVENTS_VERSION_LIST, result?.id),
      ],
    }),
    getDraftConfiguredService: build.query({
      query: ({ id }) => ({
        url: `/api/v1/core/draft-configured-service/${id}`,
      }),
      providesTags: (result, error, arg) => withIdTag(CACHE_TAG_CONFIGURED_SERVICE_DETAILS, arg?.id),
    }),
    deleteDraftConfiguredService: build.mutation({
      query: ({ id }) => ({
        url: `/api/v1/core/draft-configured-service/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [
        CACHE_TAG_CONFIGURED_SERVICES_LIST,
        CACHE_TAG_SERVICE_EXPANDABLE_VIEW,
        ...withIdTag(CACHE_TAG_CONFIGURED_SERVICE_DETAILS, arg?.id),
        ...withIdTag(CACHE_TAG_EVENTS_VERSION_LIST, arg?.id),
      ],
    }),
    patchDraftConfiguredService: build.mutation({
      query: ({ id, body }) => ({
        url: `/api/v1/core/draft-configured-service/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        CACHE_TAG_CONFIGURED_SERVICE_CONTAINER_LIST,
        CACHE_TAG_CONFIGURED_SERVICES_LIST,
        CACHE_TAG_SERVICE_EXPANDABLE_VIEW,
        ...withIdTag(CACHE_TAG_EVENTS_VERSION_LIST, arg?.id),
        ...withIdTag(CACHE_TAG_CONFIGURED_SERVICE_DETAILS, arg?.id),
        ...withIdTag(CACHE_TAG_CONFIGURED_SERVICE_EVENT_LIST, arg?.id),
      ],
    }),
    getDraftConfiguredServiceByAccountId: build.query({
      query: ({ accountId }) => ({
        url: `/api/v1/core/draft-configured-service/account/${accountId}`,
      }),
      providesTags: [CACHE_TAG_CONFIGURED_SERVICES_LIST],
    }),
    publishDraftConfiguredService: build.mutation({
      query: ({ id }) => ({
        url: `/api/v1/core/draft-configured-service/${id}/publish`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, arg) => [
        CACHE_TAG_CONFIGURED_SERVICES_LIST,
        CACHE_TAG_SERVICE_EXPANDABLE_VIEW,
        ...withIdTag(CACHE_TAG_CONFIGURED_SERVICE_DETAILS, arg?.id),
        ...withIdTag(CACHE_TAG_CONTAINER_VERSION_LIST, arg?.id),
        ...withIdTag(CACHE_TAG_EVENTS_VERSION_LIST, arg?.id),
        ...(result?.configuredServiceId
          ? withIdTag(CACHE_TAG_CONFIGURED_SERVICE_EVENT_LIST, result.configuredServiceId)
          : [CACHE_TAG_CONFIGURED_SERVICE_EVENT_LIST]),
        ...WORK_ORDER_CACHE_INVALIDATION_TAGS,
      ],
      onQueryStarted: withDelayedWorkOrderInvalidation({ api }),
    }),
    updateDraftConfiguredServiceTags: build.mutation({
      query: ({ id, body }) => ({
        url: `/api/v1/core/draft-configured-service/${id}/tags`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        CACHE_TAG_CONFIGURED_SERVICES_LIST,
        CACHE_TAG_SERVICE_EXPANDABLE_VIEW,
        ...withIdTag(CACHE_TAG_CONFIGURED_SERVICE_DETAILS, arg?.id),
      ],
    }),
    getDraftConfiguredServiceContainers: build.query({
      query: ({ configuredServiceId }) => ({
        url: `/api/v1/core/draft-configured-service/${configuredServiceId}/containers`,
        method: 'GET',
      }),
      transformResponse: stampPlacementFlagsOnDraft,
      providesTags: [CACHE_TAG_CONFIGURED_SERVICE_CONTAINER_LIST],
    }),
  }),
})

export const {
  useLazyGetDraftConfiguredServicesQuery,
  useCreateDraftConfiguredServiceMutation,
  useLazyGetDraftConfiguredServiceQuery,
  useDeleteDraftConfiguredServiceMutation,
  usePatchDraftConfiguredServiceMutation,
  useLazyGetDraftConfiguredServiceByAccountIdQuery,
  usePublishDraftConfiguredServiceMutation,
  useUpdateDraftConfiguredServiceTagsMutation,
  useLazyGetDraftConfiguredServiceContainersQuery,
} = extendedApi
